import React, { useContext, useMemo, useState } from "react";
import { WidgetContext } from "../../store/widgetContext";
import { useWidgetMsg } from "../../hooks/widget/useWidgetMsg";
import { buildRows, readPrompts } from "../../utils/promptLibrary";
import PromptRowContent from "./PromptRowContent";

/**
 * chat-experience 06c — the agent's prompt library on an empty conversation (parent §1.3 Flow 2).
 *
 * D-40: the same row list as the composer's `+` → Prompts view — the first six top-level prompts
 * (§7 step 5, D-41), then every collection as a collapsed row that expands in place. Selecting a
 * prompt raises `onSelect(entry)`, which sends immediately (D-34); opening a collection sends nothing.
 * Nothing to show (key absent, `[]`, every entry malformed) → `null`, no empty container.
 */
export default function PromptStarters({ onSelect, disabled = false }) {
	const { widget } = useContext(WidgetContext);
	const { msg, isRtl } = useWidgetMsg();
	const [openCollection, setOpenCollection] = useState(null);
	const library = useMemo(() => readPrompts(widget), [widget]);
	const rows = useMemo(() => buildRows(library, openCollection, { starters: true }), [library, openCollection]);

	if (rows.length === 0) return null;

	return (
		<nav className="prompt-starters" aria-label={msg("starters.title", "Suggested questions")} dir={isRtl ? "rtl" : undefined}>
			<ul className="prompt-starters-list">
				{rows.map((row) => (
					<li key={row.key}>
						{row.type === "collection" ? (
							<button
								type="button"
								className="prompt-starter prompt-starter--collection"
								aria-expanded={row.open}
								aria-label={msg("starters.collection", "{name}, {count} prompts", { name: row.name, count: row.count })}
								onClick={() => setOpenCollection(row.open ? null : row.name)}
							>
								<PromptRowContent row={row} />
							</button>
						) : (
							<button
								type="button"
								className={`prompt-starter${row.depth > 0 ? " prompt-starter--nested" : ""}`}
								disabled={disabled}
								aria-label={msg("starters.send", "Send: {label}", { label: row.prompt.label })}
								onClick={() => onSelect(row.prompt)}
							>
								<PromptRowContent row={row} />
							</button>
						)}
					</li>
				))}
			</ul>
		</nav>
	);
}
